import React, { ChangeEvent, forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { cssTheme } from '../../../../themes';
import minus from '../../../../utils/minus';
import { Badge } from '../../Badge';
import { FS_PRIVACY_MASK_CLASS, TextTransform } from '../../helpers/InputBase';
import { Icon } from '../../Icon';
import { CaptionStyles, FieldStyles, FieldWrapperStyles, InputLabelStyles, InputTextStyles, PrefixWrapperStyles } from '../InputText/styles';
interface Props {
  /** Field autocomplete id */
  autoComplete?: string;
  /** Message to display below input. Changes color along with validation highlights. */
  caption?: ReactNode;
  /** The options of the select element */
  children: ReactNode;
  /** An object of custom props that is not explicitly supported, but might be required for some custom implementations.
   * This can also be used to override the explicit props, like a custom onChange function.
   * or as a way to get rid of the chrome autofill.
   * */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customProps?: Record<string, any>;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** ID of this component, passed down by the controlling parent component */
  id?: string;
  /** Whether the input content is private and should be masked in FullStory */
  isPrivate?: boolean;
  /** Add an asterisk to the label */
  isRequired?: boolean;
  /** Name of this component, passed down by the controlling parent component */
  name?: string;
  /** An onFocus callback passed down by the controlling parent component */
  onFocus?: React.FocusEventHandler<HTMLSelectElement>;
  /** An onBlur callback passed down by the controlling parent component */
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;
  /** An onChange callback passed down by the controlling parent component */
  onChange?: (value: string) => void;
  /** Callback that fires when the input is clicked */
  onClick?: () => void;
  /** Content to be used as the input label and placeholder. It must be translated string */
  label?: string;
  /** The size of the input */
  size?: 'sm' | 'lg';
  /** CSS text-transform style to apply to the input field */
  textTransformType?: TextTransform;
  /** The value passed down by the controlling parent component */
  value?: string;
  /** Optional aria-label for screen-readers, used when no label is present */
  ariaLabel?: string;
  /** Put the input in the valid state */
  /** Whether the input shows or not the validation badge */
  withValidationBadge?: boolean;
  isValid?: boolean;
  /** Put the input in the invalid state */
  isInvalid?: boolean;
  /** className prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledInputSelect = styled(InputSelect)` */
  className?: string;
  /** Any component to display before the input */
  prefix?: ReactNode;
}
const _exp = /*#__PURE__*/() => ({
  prefixWidth
}) => `translateX(${minus(`${prefixWidth + 10}px`)}) translateY(-18px)`;
const _exp2 = /*#__PURE__*/() => ({
  prefixWidth
}) => `translateX(${prefixWidth + 10}px) translateY(-18px)`;
const Field = /*#__PURE__*/styled('label')({
  name: "Field",
  class: "f1qyyuuu",
  propsAsIs: false,
  vars: {
    "f1qyyuuu-0": [_exp()],
    "f1qyyuuu-1": [_exp2()]
  }
});
const InputGroupStyles = "iux5sk9";
const _exp9 = /*#__PURE__*/() => ({
  prefixWidth,
  isSmallSize
}) => {
  const gap = prefixWidth > 0 ? cssTheme.layout.spacing.s250 : '0px';
  const padding = isSmallSize ? cssTheme.layout.spacing.s300 : cssTheme.layout.spacing.s400;
  return `calc(${gap} + ${padding} + ${prefixWidth}px)`;
};
const _exp11 = /*#__PURE__*/() => ({
  textTransformType
}) => textTransformType ? textTransformType : TextTransform.none;
const InputSelectElement = /*#__PURE__*/styled('select')({
  name: "InputSelectElement",
  class: "iwupmi4",
  propsAsIs: false,
  vars: {
    "iwupmi4-0": [_exp9()],
    "iwupmi4-1": [_exp11()]
  }
});
const SuffixWrapper = /*#__PURE__*/styled('div')({
  name: "SuffixWrapper",
  class: "s1jc8qb0",
  propsAsIs: false
});
const _exp13 = /*#__PURE__*/() => ({
  isOpen
}) => isOpen ? 'rotateZ(0)' : 'rotateZ(180deg)';
const _exp14 = /*#__PURE__*/() => ({
  disabled
}) => disabled ? cssTheme.colors.input.disabled.carat : cssTheme.colors.input.default.carat;
const CaratWrapper = /*#__PURE__*/styled('div')({
  name: "CaratWrapper",
  class: "c5qvx3y",
  propsAsIs: false,
  vars: {
    "c5qvx3y-0": [_exp13()],
    "c5qvx3y-1": [_exp14()]
  }
});
export const InputSelect = forwardRef<HTMLSelectElement, Props>(({
  autoComplete,
  caption,
  children,
  dataId,
  isDisabled,
  id,
  isInvalid,
  isValid,
  isPrivate,
  name,
  onBlur,
  onChange,
  onClick,
  onFocus,
  label,
  size = 'lg',
  textTransformType = TextTransform.none,
  value,
  className,
  customProps,
  ariaLabel,
  prefix,
  isRequired,
  withValidationBadge
}, ref) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFilled, setIsFilled] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [prefixWidth, setPrefixWidth] = useState(0);
  const prefixRef = useRef<HTMLDivElement>(null);
  const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(false);
    setIsOpen(false);
    if (onBlur) {
      onBlur(event);
    }
  };
  const handleChange = (event: ChangeEvent) => {
    const newValue = (event.currentTarget as HTMLSelectElement).value;
    setIsOpen(false);
    if (onChange) {
      onChange(newValue);
    }
    setIsFilled(Boolean(newValue));
  };
  const handleFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };
  const handleClick = () => {
    if (onClick) onClick();
    setIsOpen(true);
  };
  useEffect(() => {
    setIsFilled(Boolean(value?.length));
  }, [value]);
  useEffect(() => {
    if (prefix && prefixRef.current) {
      setPrefixWidth(prefixRef.current.offsetWidth);
    }
  }, [prefix, prefixRef?.current?.offsetHeight]);
  const isSmallSize = size === 'sm';
  return <div className={cx(FieldWrapperStyles, className)}>
        <Field htmlFor={id} prefixWidth={prefixWidth} className={cx(FieldStyles, isInvalid && '__isInvalid', isFilled && '__isFilled', isDisabled && '__isDisabled', prefix && '__hasPrefix', isSmallSize && '__size-sm')}>
          <InputSelectElement autoComplete={autoComplete} data-field-error={isInvalid} data-id={dataId} disabled={isDisabled} id={id} name={name} value={value} textTransformType={textTransformType} onBlur={handleBlur} onChange={handleChange} onClick={handleClick} onFocus={handleFocus} aria-label={ariaLabel} {...customProps} className={cx(InputTextStyles, isPrivate && FS_PRIVACY_MASK_CLASS, isFilled && '__isFilled')} prefixWidth={prefixWidth} ref={ref} isSmallSize={isSmallSize}>
            {Boolean(label) && <option disabled hidden value="">
                {isFocused || isSmallSize ? label : ''}
              </option>}
            {children}
          </InputSelectElement>
          {prefix && <div className={cx(PrefixWrapperStyles)} ref={prefixRef}>
              {prefix}
            </div>}
          <div className={InputGroupStyles}>
            {label && !isSmallSize && <span className={cx(InputLabelStyles, 'label')}>
                {label}
                {isRequired && <span className="asterisk">*</span>}
              </span>}
          </div>

          <SuffixWrapper>
            {withValidationBadge && <>
                {isInvalid && <Badge iconName="WarningMark" size="xs" variant="danger" />}
                {isValid && <Badge iconName="Checkmark" size="xs" variant="success" />}
              </>}
            <CaratWrapper isOpen={isOpen} disabled={isDisabled}>
              <Icon name="CaratUp" size="xs" />
            </CaratWrapper>
          </SuffixWrapper>
        </Field>
        {caption && <div className={cx(CaptionStyles, isInvalid && '__isInvalid', isDisabled && '__isDisabled')}>
            {caption}
          </div>}
      </div>;
});
InputSelect.displayName = 'InputSelect';

require("./InputSelect.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./InputSelect.tsx");